import React, { useState, useEffect } from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../../api/auth';
import PageLayout from '../../components/layout/PageLayout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BasicButton from '../../components/button/BasicButton';
import theme from '../../theme';
import CustomTextField from '../../components/common/CustomTextField';

const schema = yup.object().shape({
  password: yup
    .string()
    .required('비밀번호를 입력해 주세요.')
    .min(8, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .max(20, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
      '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.',
    ),
  confirmPassword: yup
    .string()
    .required('비밀번호 확인을 입력해 주세요.')
    .oneOf([yup.ref('password')], '비밀번호가 일치하지 않습니다.'),
});

const PasswordReset = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [token, setToken] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // URL에서 토큰 가져오기
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const resetToken = urlParams.get('token');

    if (!resetToken) {
      navigate('/not-found');
      return;
    }
    setToken(resetToken);
  }, [location, navigate]);

  const onSubmit = async (formData) => {
    try {
      await authApi.resetPassword(token, formData.password);
      navigate('/login');
    } catch (err) {
      console.error(err);
    } finally {
      reset();
    }
  };

  return (
    <PageLayout>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: { lg: '32.5%', md: '49%', xs: '100%' },
            minWidth: { xs: '320px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack sx={{ width: '100%' }} spacing={'40px'}>
            <Stack spacing={'16px'}>
              <Typography
                fontSize={{ lg: 24, md: 22, xs: 20 }}
                fontWeight="bold"
                textAlign="center"
              >
                비밀번호 재설정
              </Typography>
              <Typography
                fontSize="14px"
                textAlign="center"
                color={theme.palette.black[4]}
                align="center"
              >
                새로운 비밀번호를 설정해 주세요.
              </Typography>
            </Stack>

            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3">
                  비밀번호<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  placeholder="문자, 숫자, 특수문자 포함 8~20자"
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3">
                  비밀번호 확인<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  placeholder="비밀번호 재입력"
                />
              </Stack>
            </Stack>
          </Stack>

          <Box mt={'30px'} sx={{ width: '100%' }}>
            <BasicButton
              type="submit"
              fullWidth
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              재설정
            </BasicButton>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default PasswordReset;
